import { Injectable } from '@angular/core';
import { Observable, forkJoin, map, tap } from 'rxjs';
import { FileDatasetValidationStatus, FileModel, MlModel } from '../../models';
import { ValidateDatasetFileResult } from '../../models/validate-dataset-file-result.model';
import { ValidateSegmentationFileResult } from '../../models/validate-segmentation-file-result.model';
import { ExperimentalFeaturesService } from '../experimental-features.service';
import { MachineLearningService } from '../machine-learning.service';

@Injectable({
    providedIn: 'root',
})
export class CziValidatorService {
    private validations = new Map<string, FileModel[]>();

    constructor(
        private machineLearningService: MachineLearningService,
        private experimentalFeatures: ExperimentalFeaturesService,
    ) {}

    /**
     * Validates the provided files based on the operation context.
     * Supports both dataset and segmentation validations.
     *
     * @param operationId - The unique ID for the validation operation.
     * @param files - The list of files to validate.
     * @param isRetry - Indicates if the validation is a retry operation.
     * @param isSegmentation - Determines if segmentation validation should be applied.
     * @param selectedModel - The selected machine learning model for segmentation.
     * @returns Observable emitting the list of files with updated validation statuses.
     */
    validate$(
        operationId: string,
        files: FileModel[],
        isRetry: boolean = false,
        isSegmentation?: boolean,
        selectedModel?: MlModel | null,
    ): Observable<FileModel[]> {
        if (isRetry) {
            this.validations.delete(operationId);
        }

        this.addFilesToFilesInValidation(operationId, files);

        const validationObservable$: Observable<ValidateDatasetFileResult[] | ValidateSegmentationFileResult[]> = isSegmentation
            ? this.validateSegmentationFiles$(files, selectedModel)
            : this.validateDatasetFiles$(operationId, files);

        return validationObservable$.pipe(
            tap((validatedResults: ValidateDatasetFileResult[] | ValidateSegmentationFileResult[]): void => {
                this.removeValidatedFilesFromFilesInValidation(operationId, validatedResults);
                if (isSegmentation) {
                    this.updateFileSegmentationStatuses(files, validatedResults as ValidateSegmentationFileResult[]);
                } else {
                    this.updateFileDatasetStatuses(files, validatedResults as ValidateDatasetFileResult[]);
                }
            }),
            map((): FileModel[] => files),
        );
    }

    areSomeFilesPendingValidationOrUnknown(files: FileModel[] | FileDatasetValidationStatus[]): boolean {
        if (!this.experimentalFeatures.isCziConversionFeatureEnabled) {
            return false;
        }

        return files.some(f => {
            return f instanceof FileModel
                ? files.some(f => f.validationStatus != null && (f.validationStatus === 'PendingValidation' || f.validationStatus === 'Unknown'))
                : files.some(f => f.status != null && (f.status === 'Active' || f.status === 'Unknown'));
        });
    }

    /**
     * Initiates segmentation validation for the provided files.
     *
     * @param files - The list of files to validate for segmentation.
     * @param selectedModel - The selected machine learning model used for segmentation.
     * @returns Observable emitting the results of the segmentation validation.
     */
    private validateSegmentationFiles$(files: FileModel[], selectedModel?: MlModel | null): Observable<ValidateSegmentationFileResult[]> {
        const requests = files.map(file =>
            this.machineLearningService.validateSegmentationFile$(selectedModel?.trainingType as string, {
                fileId: file.id,
                trainingId: selectedModel?.trainingId as string,
            }),
        );
        return forkJoin(requests);
    }

    /**
     * Initiates dataset validation for the provided files.
     *
     * @param operationId - The unique ID for the validation operation.
     * @param files - The list of files to validate.
     * @returns Observable emitting the results of the dataset validation.
     */
    private validateDatasetFiles$(operationId: string, files: FileModel[]): Observable<ValidateDatasetFileResult[]> {
        return this.machineLearningService.validateFiles$(
            operationId,
            files.map(file => file.id),
        );
    }

    /**
     * Updates the validation status of files based on segmentation validation results.
     *
     * @param files - The list of files being validated.
     * @param results - The results of the segmentation validation.
     */
    private updateFileSegmentationStatuses(files: FileModel[], results: ValidateSegmentationFileResult[]): void {
        results.forEach(result => {
            const file = files.find(f => f.id === result.fileId);
            if (file) {
                file.validationStatus = result.fileSegmentationValidationStatus;
            }
        });
    }

    /**
     * Updates the validation status of files based on dataset validation results.
     *
     * @param files - The list of files being validated.
     * @param results - The results of the dataset validation.
     */
    private updateFileDatasetStatuses(files: FileModel[], results: ValidateDatasetFileResult[]): void {
        results.forEach(result => {
            const file = files.find(f => f.id === result.fileId);
            if (file) {
                file.validationStatus = result.fileDatasetValidationStatus;
            }
        });
    }

    /**
     * Adds files to the validation queue for a given operation ID.
     *
     * @param operationId - The unique identifier for the validation operation.
     * @param files - The files to add to the validation queue.
     */
    private addFilesToFilesInValidation(operationId: string, files: FileModel[]): void {
        const filesInValidationForThisOperationId = this.validations.get(operationId);
        if (filesInValidationForThisOperationId && filesInValidationForThisOperationId.length === 0) {
            this.validations.set(operationId, files);
        } else if (filesInValidationForThisOperationId && filesInValidationForThisOperationId.length > 0) {
            const newListOfFilesInValidationForThisOperationId = filesInValidationForThisOperationId;
            for (const file of files) {
                if (filesInValidationForThisOperationId.find((fileInValidation: FileModel): boolean => fileInValidation.id === file.id) === null) {
                    newListOfFilesInValidationForThisOperationId.push(file);
                }
            }
            this.validations.set(operationId, newListOfFilesInValidationForThisOperationId);
        }
    }

    /**
     * Removes files from the validation queue after they have been validated.
     *
     * @param operationId - The unique identifier for the validation operation.
     * @param result - The validation results, either for datasets or segmentation files.
     */
    private removeValidatedFilesFromFilesInValidation(operationId: string, result: ValidateDatasetFileResult[] | ValidateSegmentationFileResult[]): void {
        const filesInValidationForThisOperationId = this.validations.get(operationId);
        if (filesInValidationForThisOperationId) {
            const filesInValidationUpdated = [...filesInValidationForThisOperationId];
            for (const element of result) {
                const indexOfElement = filesInValidationUpdated.findIndex((fileInValidation: FileModel): boolean => fileInValidation.id === element.fileId);
                if (indexOfElement !== -1) {
                    filesInValidationUpdated.splice(indexOfElement, 1);
                }
            }
            if (filesInValidationUpdated.length > 0) {
                this.validations.set(operationId, filesInValidationUpdated);
            } else {
                this.validations.delete(operationId);
            }
        }
    }
}
